import type { ConfigurationContextValue } from "../root/ConfigurationContext";
import type { Contact, LabeledValue } from "../types";

export type ContactsByStage = Record<string, Contact[]>;

const stageNumberPrefix = /^\s*(\d+)(?:[.)]|\s|$)/;

const getStageOrder = (stage: LabeledValue, index: number) => {
  if (typeof stage.order === "number" && Number.isFinite(stage.order)) {
    return stage.order;
  }

  const numericPrefix = stage.label.match(stageNumberPrefix)?.[1];
  if (numericPrefix) {
    return Number(numericPrefix);
  }

  return index + 1;
};

export const getOrderedPipelineStages = (
  connectionPipelineStages:
    | ConfigurationContextValue["connectionPipelineStages"]
    | undefined,
  activeStageValues: string[] = [],
) => {
  const configuredStages = (connectionPipelineStages ?? []).map(
    (stage, index) => ({
      ...stage,
      order: getStageOrder(stage, index),
      originalIndex: index,
    }),
  );

  const highestConfiguredOrder = configuredStages.reduce(
    (highest, stage) => Math.max(highest, stage.order),
    0,
  );

  const missingActiveStages = activeStageValues
    .filter(
      (stageValue, index, values) =>
        stageValue &&
        values.indexOf(stageValue) === index &&
        !configuredStages.some((stage) => stage.value === stageValue),
    )
    .map((stageValue, index) => ({
      value: stageValue,
      label: stageValue,
      order: highestConfiguredOrder + index + 1,
      originalIndex: configuredStages.length + index,
    }));

  const orderedStages = [...configuredStages, ...missingActiveStages].sort(
    (stageA, stageB) =>
      stageA.order - stageB.order ||
      stageA.originalIndex - stageB.originalIndex,
  );

  return orderedStages.map(
    ({ originalIndex: _originalIndex, ...stage }) => stage,
  );
};

export const getContactsByStage = (
  unorderedContacts: Contact[],
  connectionPipelineStages: ConfigurationContextValue["connectionPipelineStages"],
) => {
  if (!connectionPipelineStages) return {};
  const orderedPipelineStages = getOrderedPipelineStages(
    connectionPipelineStages,
  );
  const contactsByStage: ContactsByStage = unorderedContacts.reduce(
    (acc, contact) => {
      // if contact has a stage that does not exist in configuration, assign it to the first stage
      const stage = orderedPipelineStages.find(
        (s) => s.value === contact.connection_pipeline_stage,
      )
        ? contact.connection_pipeline_stage || orderedPipelineStages[0].value
        : orderedPipelineStages[0].value;
      acc[stage].push(contact);
      return acc;
    },
    orderedPipelineStages.reduce(
      (obj, stage) => ({ ...obj, [stage.value]: [] }),
      {} as ContactsByStage,
    ),
  );
  // order each column by index
  orderedPipelineStages.forEach((stage) => {
    contactsByStage[stage.value] = contactsByStage[stage.value].sort(
      (recordA: Contact, recordB: Contact) =>
        (recordA.index || 0) - (recordB.index || 0),
    );
  });
  return contactsByStage;
};
